import { createContext } from 'react'
import { supabase } from './supabase'

/**
 * GUIDES (339, HLP-45; One and Help v2 S3) - a training guide per function,
 * shown by role, at #/help/guides/:guide/:section.
 *
 * The words live in `guides/<key>.md` in this repo, loaded by migration
 * (lib/guideMarkdown.ts is the format). The SERVER decides who reads which
 * guide - `app.may_read_guide`, one function - and `guides()` returns only
 * what the caller may read. Nothing here gates a guide; the screen groups
 * what came back. Every act a guide describes is still checked by the server
 * for the person who tries it.
 *
 * D5, REVISED 1 Nov late (David; 353): a guide tied to a role is read ONLY by
 * the people who hold it. An audience is "can open the pages this guide
 * teaches", so a shop admin reads the store-manager guide and a Gallery admin
 * the viewer guide; 'everyone' guides are open to all. Support-desk holders
 * and tenant administrators read every guide (assumed; David may veto) - they
 * are the only people who ever receive "Other teams' guides". `yours` says
 * which are FOR your roles; p_all is ignored and this file always sends false.
 */

export type GuideAudience =
  | 'everyone' | 'store_manager' | 'shop_admin' | 'support_desk' | 'comms_admin'
  // 353: declared for the Gallery guides to come (none written yet)
  | 'gallery_viewer' | 'gallery_doc_user' | 'gallery_admin'

export type GuideSectionRow = { key: string; title: string; body: string; locale: string }

export type GuideRow = {
  key: string
  audience: GuideAudience
  sort: number
  /** FOR one of your roles (or for everyone). False = another team's guide,
   *  which only a support-desk holder or tenant administrator receives (353). */
  yours: boolean
  locale: string
  title: string
  lede: string
  sections: GuideSectionRow[]
}

/** The two store guides, which used to live at #/admin/shop/guide(/admin). */
export const STORE_GUIDE = 'store-manager'
export const SHOP_GUIDE = 'shop-admin'

/**
 * WHAT THE GUIDE PAGE HANDS ASK ONE (S5, HLP-47): a chip naming the guide and
 * the section being read, and starters that are that guide's own section
 * titles (the one being read first). The chip's key is the section's route
 * without the leading slash - the shell sends it to One as `/<key>` with the
 * title, as WORDS (S4), never a permission. Pure, so the suite checks it.
 */
export function guideAskContext(
  g: Pick<GuideRow, 'key' | 'title' | 'sections'>,
  section: string | null,
): { page: { key: string; title: string }; starters: string[] } {
  const at = section ? g.sections.find((s) => s.key === section) ?? null : null
  const titles = g.sections.map((s) => s.title)
  const starters = at ? [at.title, ...titles.filter((t) => t !== at.title)] : titles
  return {
    page: {
      key: at ? `help/guides/${g.key}/${at.key}` : `help/guides/${g.key}`,
      title: (at ? at.title : g.title).slice(0, 60),
    },
    starters: starters.slice(0, 3),
  }
}

/** The route of a guide, optionally at one section. */
export function guideHref(guide: string, section?: string | null): string {
  return section ? `#/help/guides/${guide}/${section}` : `#/help/guides/${guide}`
}

/**
 * THE GUIDE SHEET'S ADDRESS (HLP-49, David 1 Nov). A "How … works" link on a
 * working page opens ONE section beside the work, not the guides page. The
 * sheet lives in the page's own query - #/admin/shop?guide=store-manager&
 * section=count - so Back closes it, a reload reopens it and a shared link
 * opens the page with the sheet. A link is a request: keys that are not plain
 * keys are ignored, and whether the guide may be read is the server's
 * (`guides()` returns only what the caller may read).
 */
export type GuideSheetTarget = { guide: string; section: string }

const GUIDE_KEY = /^[a-z0-9-]{1,40}$/

export function guideSheetTarget(search: URLSearchParams): GuideSheetTarget | null {
  const guide = search.get('guide')
  const section = search.get('section')
  return guide && section && GUIDE_KEY.test(guide) && GUIDE_KEY.test(section) ? { guide, section } : null
}

/** The page's search with the sheet set (or cleared, with null); every other
 *  parameter the page carries is kept. Returns `?…` or '' (no query). */
export function guideSheetSearch(search: URLSearchParams | string, target: GuideSheetTarget | null): string {
  const q = new URLSearchParams(search)
  q.delete('guide')
  q.delete('section')
  if (target) {
    q.set('guide', target.guide)
    q.set('section', target.section)
  }
  const s = q.toString()
  return s ? `?${s}` : ''
}

/** What the sheet shows for a target, from the rows guides() returned. */
export type GuideSheetState =
  | { kind: 'loading' }
  | { kind: 'refused' }
  /** guides() itself failed: said in the same one line, never as an error page. */
  | { kind: 'failed' }
  | { kind: 'section'; guide: GuideRow; section: GuideSectionRow }

export function resolveGuideSheet(rows: readonly GuideRow[] | null, target: GuideSheetTarget): GuideSheetState {
  if (rows === null) return { kind: 'loading' }
  const guide = rows.find((g) => g.key === target.guide)
  const section = guide?.sections.find((s) => s.key === target.section)
  return guide && section ? { kind: 'section', guide, section } : { kind: 'refused' }
}

/** The DOM id a section renders under - what a section route scrolls to. */
export function guideSectionDomId(guide: string, section: string): string {
  return `guide-${guide}-${section}`
}

/**
 * Where the retired #/admin/shop/guide routes now go. `?s=<section>` was the
 * old deep link; it becomes the section segment when it is a plain key, and
 * is dropped otherwise (a link is a request).
 */
export function legacyShopGuidePath(admin: boolean, s: string | null): string {
  const key = admin ? SHOP_GUIDE : STORE_GUIDE
  return s && /^[a-z0-9-]{1,40}$/.test(s) ? `/help/guides/${key}/${s}` : `/help/guides/${key}`
}

/**
 * The left-hand list (349, D5): For your roles first, then For everyone, then
 * Other teams' guides. Every row the server returned is in exactly one group;
 * nothing is dropped and nothing is re-judged - `yours` is the server's.
 */
export function groupGuides(rows: readonly GuideRow[]): {
  roles: GuideRow[]
  everyone: GuideRow[]
  others: GuideRow[]
} {
  return {
    roles: rows.filter((g) => g.audience !== 'everyone' && g.yours),
    everyone: rows.filter((g) => g.audience === 'everyone'),
    others: rows.filter((g) => g.audience !== 'everyone' && !g.yours),
  }
}

/** Every guide the caller may read. p_all is sent false and the server ignores
 *  it either way (349): there is nothing left for it to ask for. */
export async function loadGuides(locale: string | undefined): Promise<GuideRow[]> {
  const { data, error } = await supabase.rpc('guides', { p_locale: locale ?? null, p_all: false })
  if (error) throw error
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    key: r.guide_key as string,
    audience: r.audience as GuideAudience,
    sort: r.sort_order as number,
    yours: r.yours === true,
    locale: r.locale as string,
    title: r.title as string,
    lede: r.lede as string,
    sections: ((r.sections as GuideSectionRow[] | null) ?? []).map((s) => ({
      key: s.key,
      title: s.title,
      body: s.body,
      locale: s.locale,
    })),
  }))
}

/**
 * WHICH "HOW … WORKS" LINKS MAY BE DRAWN (353, HLP-50). The set of
 * "<guide>/<section>" this person's guides() read returned. A GuideLink
 * renders only for a key in it - never a link to a refusal - and renders
 * nothing while the read is pending (null) or outside the shell. The server
 * decided who reads what; this only remembers what it said.
 */
export function readableGuideSections(rows: readonly GuideRow[]): ReadonlySet<string> {
  return new Set(rows.flatMap((g) => g.sections.map((s) => `${g.key}/${s.key}`)))
}

export const ReadableGuidesContext = createContext<ReadonlySet<string> | null>(null)
